/**
 * REUSABLE COMPONENT - ProtectedRoute
 * Route wrapper that requires authentication
 * Can be used in any React project with React Router
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Loader from './Loader';

const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredPermission = null,
  redirectTo = '/login',
  fallback = null,
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (loading) {
    return fallback || <Loader fullScreen text="Chargement..." />;
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check required role
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.includes(user?.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-6xl font-bold text-gray-900 mb-4">403</h1>
            <p className="text-xl text-gray-600 mb-8">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
            <a
              href="/dashboard"
              className="text-blue-600 hover:text-blue-500 font-medium"
            >
              Retour au tableau de bord
            </a>
          </div>
        </div>
      );
    }
  }

  // Check required permission
  if (requiredPermission) {
    const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasPermission = permissions.some(
      (perm) => user?.permissions?.includes(perm) || user?.permissions?.includes('all')
    );

    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-6xl font-bold text-gray-900 mb-4">403</h1>
            <p className="text-xl text-gray-600 mb-8">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
            <a
              href="/dashboard"
              className="text-blue-600 hover:text-blue-500 font-medium"
            >
              Retour au tableau de bord
            </a>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
